/**
 * Legal document overlay for the landing page — opens Terms of Service /
 * Privacy Policy / Cookie Policy as a "case file" over the marketing screen.
 *
 * Documents come live from the core CMS (GET /api/cms/legal/:slug, edited in
 * the admin panel); `{{company.*}}` placeholders inside them are substituted
 * from the admin-managed legal-entity settings, so a change in Admin → Game
 * Settings shows up here with no copy edits.
 */
import { fetchLegalPage, fillCompanyVars, type CompanyInfo } from '../game/legal-api';
import { t, onLocaleChange } from '../i18n';

const closers = new WeakMap<HTMLElement, () => void>();

/** Close an open legal overlay (used by the landing teardown so the Esc listener never leaks). */
export function closeLegalDoc(root: HTMLElement): void {
  const wrap = root.querySelector<HTMLElement>('.lglv');
  if (wrap) closers.get(wrap)?.();
}

export function openLegalDoc(
  root: HTMLElement,
  slug: string,
  company: () => Promise<CompanyInfo | null>,
): void {
  // one at a time — re-use the mounted overlay if a doc is already open
  const existing = root.querySelector<HTMLElement>('.lglv');
  if (existing) {
    void loadInto(existing, slug, company);
    return;
  }

  const wrap = document.createElement('div');
  wrap.className = 'lglv';
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-modal', 'true');
  wrap.innerHTML =
    '<div class="lglv-back"></div>' +
    '<article class="lglv-doc" tabindex="-1">' +
    `  <button class="lglv-x" aria-label="${t('common.close')}">&times;</button>` +
    `  <p class="lglv-kicker">${t('legalOverlay.kicker')}</p>` +
    '  <h1 class="lglv-title"></h1>' +
    `  <p class="lglv-stamp" aria-hidden="true">${t('legalOverlay.stamp')}</p>` +
    '  <div class="lglv-rule"></div>' +
    `  <div class="lglv-body"><p class="lglv-wait">${t('legalOverlay.loading')}</p></div>` +
    '</article>';
  root.appendChild(wrap);

  const close = () => {
    document.removeEventListener('keydown', onKey);
    wrap.remove();
  };
  closers.set(wrap, close);
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  wrap.querySelector('.lglv-back')!.addEventListener('click', close);
  wrap.querySelector('.lglv-x')!.addEventListener('click', close);

  // cross-links between policies inside the document body
  wrap.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest<HTMLElement>('[data-legal]');
    if (!link) return;
    e.preventDefault();
    e.stopPropagation(); // keep the SPA-wide link interceptor from re-routing under the overlay
    void loadInto(wrap, link.dataset.legal!, company);
  });

  void loadInto(wrap, slug, company);
}

async function loadInto(
  wrap: HTMLElement,
  slug: string,
  company: () => Promise<CompanyInfo | null>,
): Promise<void> {
  const title = wrap.querySelector<HTMLElement>('.lglv-title')!;
  const body = wrap.querySelector<HTMLElement>('.lglv-body')!;
  wrap.dataset.state = 'loading';
  title.textContent = '';
  body.innerHTML = `<p class="lglv-wait">${t('legalOverlay.loading')}</p>`;
  try {
    const [page, info] = await Promise.all([fetchLegalPage(slug), company()]);
    title.textContent = page.title;
    body.innerHTML = fillCompanyVars(page.content, info);
    wrap.querySelector<HTMLElement>('.lglv-doc')?.scrollTo({ top: 0 });
    wrap.dataset.state = 'loaded';
  } catch {
    wrap.dataset.state = 'error';
    title.textContent = t('legalOverlay.errorTitle');
    body.innerHTML = `<p class="lglv-wait">${t('legalOverlay.errorBody')}</p>`;
  }
}

/**
 * The static chrome (kicker, stamp, close label) and the loading/error
 * placeholders are stamped once from a template string when the overlay
 * mounts, so a live language switch would otherwise leave an already-open
 * overlay showing stale copy. `wrap.dataset.state` (set in `loadInto`) tells
 * us whether to repaint the loading/error placeholder or leave it alone — the
 * fetched document's title/body is admin-authored CMS content, not part of
 * this catalogue, so it is never touched here.
 */
onLocaleChange(() => {
  document.querySelectorAll<HTMLElement>('.lglv').forEach((wrap) => {
    wrap.querySelector<HTMLElement>('.lglv-x')?.setAttribute('aria-label', t('common.close'));
    const kicker = wrap.querySelector<HTMLElement>('.lglv-kicker');
    if (kicker) kicker.textContent = t('legalOverlay.kicker');
    const stamp = wrap.querySelector<HTMLElement>('.lglv-stamp');
    if (stamp) stamp.textContent = t('legalOverlay.stamp');

    const wait = wrap.querySelector<HTMLElement>('.lglv-wait');
    if (wrap.dataset.state === 'error') {
      const title = wrap.querySelector<HTMLElement>('.lglv-title');
      if (title) title.textContent = t('legalOverlay.errorTitle');
      if (wait) wait.textContent = t('legalOverlay.errorBody');
    } else if (wrap.dataset.state === 'loading' && wait) {
      wait.textContent = t('legalOverlay.loading');
    }
  });
});
